// lib/safeRedirectPath.ts
//
// ONE answer to "may I send a freshly authenticated user here".
//
// The check was `value.startsWith('/')`, written in three places. It is very nearly right and
// wrong in exactly one way: `//evil.com` starts with a slash, and a browser reads it as
// protocol-relative, so it navigates OFF-SITE. Sending someone off-site the instant they sign in is
// the moment they are least likely to look at the address bar, which is what makes it worth the
// twenty lines rather than a comment saying "be careful here".
//
// `\` counts too: browsers normalise a backslash to a forward slash in the authority position, so
// `/\evil.com` and `\/evil.com` reach the same place as `//evil.com`.
//
// Anything absolute is refused rather than parsed. An allowlist of hosts would be the other design,
// and it is worse here: every legitimate destination in this app is in-app, so accepting a host at
// all buys nothing and adds a list to keep correct.

/** The destination for anything refused: the app's own root. */
export const SAFE_REDIRECT_FALLBACK = '/';

/**
 * The path to navigate to after sign-in or sign-up, or `/` when the input cannot be trusted.
 *
 * Returns a PATH, never a URL. Callers hand it straight to the router.
 */
export const safeRedirectPath = (value: unknown): string => {
  if (typeof value !== 'string') return SAFE_REDIRECT_FALLBACK;

  const trimmed = value.trim();
  if (trimmed.length === 0) return SAFE_REDIRECT_FALLBACK;

  // Must be rooted. This also refuses "evil.com", which a router would resolve relative to the
  // current page rather than off-site, but which is never a destination we mean.
  if (!trimmed.startsWith('/')) return SAFE_REDIRECT_FALLBACK;

  // Protocol-relative, in both slash flavours: the whole point of this module.
  const second = trimmed.charAt(1);
  if (second === '/' || second === '\\') return SAFE_REDIRECT_FALLBACK;

  // A control character can truncate the value inside a header or a URL parser, so what the browser
  // acts on stops being what was checked here.
  // eslint-disable-next-line no-control-regex -- the point is to reject exactly these bytes
  if (/[\u0000-\u001F\u007F]/.test(trimmed)) return SAFE_REDIRECT_FALLBACK;

  return trimmed;
};
